'use client'

import type { PostHog } from 'posthog-js'
import posthog from 'posthog-js'

/**
 * Mock PostHog client that is safe to use when the real API key is unavailable.
 * Prevents crashes from posthog?.capture() etc. when the client was never init()'d.
 */
const mockPostHog = {
  __loaded: true,
  capture: () => {},
  identify: () => {},
  reset: () => {},
  opt_out_capturing: () => {},
  opt_in_capturing: () => {},
  onFeatureFlags: (cb: () => void) => {
    cb()
  },
  getFeatureFlag: () => undefined,
  isFeatureEnabled: () => false,
  reloadFeatureFlags: () => {},
  people: { set: () => {} },
} as unknown as PostHog

/**
 * Initialize PostHog browser client.
 * Returns a mock (no-op) client when NEXT_PUBLIC_POSTHOG_KEY is missing or a
 * placeholder, so that every `posthog?.capture()` call downstream stays safe.
 */
export function initPostHog(): PostHog {
  if (typeof window === 'undefined') {
    return mockPostHog
  }

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY || 'PLACEHOLDER_KEY'
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'

  if (!key || key === 'PLACEHOLDER_KEY') {
    return mockPostHog
  }

  // LGPD compliance: start opted-out until user explicitly accepts cookies.
  // The CookieConsentBanner calls posthog.opt_in_capturing() after user consent.
  const cookieConsent =
    typeof window !== 'undefined' ? localStorage.getItem('cookie_consent') : null
  const storedOptOut = cookieConsent !== 'accepted'

  posthog.init(key, {
    api_host: host,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    opt_out_capturing_by_default: storedOptOut,
  })

  // If user already accepted cookies previously, re-enable capturing
  if (typeof window !== 'undefined' && localStorage.getItem('cookie_consent') === 'accepted') {
    posthog.opt_in_capturing()
  }

  return posthog
}

export const phClient = typeof window !== 'undefined' ? posthog : null
